import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Shield, Plus, Minus, AlertTriangle, CheckCircle, Loader2, RefreshCw, ArrowRight } from 'lucide-react';
import { ethers } from 'ethers';
import { CONTRACT_ADDRESSES } from '../config/contracts';
// PostHog tracking will be added when admin actions are implemented

// Helper function to get RPC provider with fallback
const getRpcProvider = (chainId, fallbackProvider) => {
  if (chainId === 56) {
    // BSC Mainnet - use public RPC to avoid rate limiting
    return new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org');
  } else if (chainId === 20250217) {
    // Xphere Mainnet
    return new ethers.JsonRpcProvider('https://xp-mainnet.rpc.xplorium.xyz');
  }
  return fallbackProvider;
};

// Contract ABIs
const DESTINATION_BRIDGE_ABI = [
  "function addTokenReserves(address token, uint256 amount)",
  "function removeTokenReserves(address token, uint256 amount)",
  "function getTokenReserve(address token) view returns (uint256)",
  "function owner() view returns (address)",
  "function transferOwnership(address newOwner)",
  "function setDailyMintLimit(address token, uint256 limit) external",
  "function dailyMintLimit(address token) external view returns (uint256)",
  "function getRemainingDailyMintLimit(address token) external view returns (uint256)",
  "function getCurrentDailyMinted(address token) external view returns (uint256)"
];

const BRIDGE_SOURCE_ABI = [
  "function setFeeCollector(address newCollector)",
  "function feeCollector() view returns (address)",
  "function setBridgeFee(uint256 newFee)",
  "function bridgeFee() view returns (uint256)",
  "function owner() view returns (address)",
  "function emergencyWithdraw(address token, uint256 amount)",
  "function setTokenLimits(address token, uint256 minAmount, uint256 maxAmount, uint256 dailyLimit)",
  "function getTokenInfo(address token) view returns (bool supported, uint8 decimals, uint256 minAmount, uint256 maxAmount, uint256 dailyLimit, uint256 dailyUsage, uint256 lastResetDay)",
  "function transferOwnership(address newOwner)"
];

const ERC20_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

const AdminPanel = ({ provider, signer, account, chainId, onNetworkSwitch, onNetworkSwitchComplete }) => {
  const [loading, setLoading] = useState(false);
  const [reserves, setReserves] = useState({});
  const [balances, setBalances] = useState({});
  const [amounts, setAmounts] = useState({});
  const [isOwner, setIsOwner] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('bsc'); // 'bsc' or 'xphere'
  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
  const [reserveMode, setReserveMode] = useState('add'); // 'add' or 'remove'
  const [bridgeFee, setBridgeFee] = useState(0);
  const [newBridgeFee, setNewBridgeFee] = useState('');
  const [emergencyWithdrawAmount, setEmergencyWithdrawAmount] = useState('');
  const [lockedTokenBalance, setLockedTokenBalance] = useState('0');
  const [tokenDecimals, setTokenDecimals] = useState({});
  const [tokenLimits, setTokenLimits] = useState({});
  const [newLimits, setNewLimits] = useState({
    minAmount: '',
    maxAmount: '',
    dailyLimit: ''
  });
  const [destinationDailyMintLimits, setDestinationDailyMintLimits] = useState({});
  const [newDestinationDailyMintLimit, setNewDestinationDailyMintLimit] = useState('');
  const [ownerAddresses, setOwnerAddresses] = useState({});
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState(''); // 'usdt', 'usdx', or 'rwaNova'
  const hasLoadedData = useRef(false);

  // Network configurations
  const networks = useMemo(() => ({
    bsc: {
      chainId: 56,
      name: 'BSC Mainnet',
      rpcUrl: 'https://bsc-dataseed.binance.org',
      bridge: CONTRACT_ADDRESSES[56].destBridge,
      bridgeSource: CONTRACT_ADDRESSES[56].sourceBridge,
      token: CONTRACT_ADDRESSES[56].usdt,
      tokenSymbol: 'USDT',
      tokenDecimals: 6 // Will be updated dynamically from contract
    },
    xphere: {
      chainId: 20250217,
      name: 'Xphere Mainnet',
      rpcUrl: 'https://xp-mainnet.rpc.xplorium.xyz',
      bridge: CONTRACT_ADDRESSES[20250217].destBridge,
      bridgeSource: CONTRACT_ADDRESSES[20250217].sourceBridge,
      token: CONTRACT_ADDRESSES[20250217].usdx,
      tokenSymbol: 'USDX',
      tokenDecimals: 6 // Will be updated dynamically from contract
    }
  }), []);

  // Use centralized contract addresses - removed as we use networks object instead
  // const contracts = useMemo(() => ({
  //   56: { // BSC Mainnet
  //     bridge: CONTRACT_ADDRESSES[56].destBridge,
  //     usdt: CONTRACT_ADDRESSES[56].usdt
  //   },
  //   20250217: { // Xphere Mainnet
  //     bridge: CONTRACT_ADDRESSES[20250217].destBridge,
  //     usdx: CONTRACT_ADDRESSES[20250217].usdx
  //   }
  // }), []);

  // const currentContract = contracts[chainId]; // Removed - using currentNetwork instead
  const currentNetwork = networks[selectedNetwork];

  // Get available tokens for the current network
  const getAvailableTokens = useCallback((networkKey) => {
    const tokens = [];
    const contracts = CONTRACT_ADDRESSES[networks[networkKey]?.chainId];
    
    if (!contracts) return tokens;
    
    if (networkKey === 'bsc') {
      if (contracts.usdt) {
        tokens.push({
          key: 'usdt',
          symbol: 'USDT',
          name: process.env.REACT_APP_USDT_NAME || 'Tether USD',
          address: contracts.usdt,
          decimals: parseInt(process.env.REACT_APP_USDT_DECIMALS || '6')
        });
      }
      if (contracts.rwaNova) {
        tokens.push({
          key: 'rwaNova',
          symbol: 'RWA Nova',
          name: process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova',
          address: contracts.rwaNova,
          decimals: parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18')
        });
      }
    } else if (networkKey === 'xphere') {
      if (contracts.usdx) {
        tokens.push({
          key: 'usdx',
          symbol: 'USDX',
          name: process.env.REACT_APP_USDX_NAME || 'USDX',
          address: contracts.usdx,
          decimals: parseInt(process.env.REACT_APP_USDX_DECIMALS || '6')
        });
      }
      if (contracts.rwaNova) {
        tokens.push({
          key: 'rwaNova',
          symbol: 'RWA Nova',
          name: process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova',
          address: contracts.rwaNova,
          decimals: parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18')
        });
      }
    }
    
    return tokens;
  }, [networks]);

  const availableTokens = useMemo(() => getAvailableTokens(selectedNetwork), [selectedNetwork, getAvailableTokens]);

  // Get current selected token info
  const currentToken = useMemo(() => {
    if (!selectedToken || !availableTokens.length) return null;
    return availableTokens.find(t => t.key === selectedToken) || availableTokens[0];
  }, [selectedToken, availableTokens]);

  // Auto-select first token when network or available tokens change
  useEffect(() => {
    if (availableTokens.length > 0 && (!selectedToken || !availableTokens.find(t => t.key === selectedToken))) {
      setSelectedToken(availableTokens[0].key);
      hasLoadedData.current = false; // Reset to reload data for new token
    }
  }, [availableTokens, selectedToken]);

  // Reload data when token changes
  useEffect(() => {
    if (currentToken && provider && signer && account && currentNetwork) {
      hasLoadedData.current = false;
      setIsLoadingData(false);
    }
  }, [currentToken?.key, currentNetwork?.chainId, provider, signer, account]);
  
  // Debug logs - moved to useEffect to prevent infinite loops
  useEffect(() => {
    console.log('🔍 AdminPanel Debug:');
    console.log('  selectedNetwork:', selectedNetwork);
    console.log('  chainId:', chainId);
    console.log('  currentNetwork:', currentNetwork);
    console.log('  provider:', !!provider);
    console.log('  signer:', !!signer);
    console.log('  account:', account);
    console.log('  isLoadingData:', isLoadingData);
    console.log('  CONTRACT_ADDRESSES:', CONTRACT_ADDRESSES);
    console.log('  USDT Address (BSC):', CONTRACT_ADDRESSES[56]?.usdt);
    console.log('  USDX Address (Xphere):', CONTRACT_ADDRESSES[20250217]?.usdx);
  }, [selectedNetwork, chainId, currentNetwork, provider, signer, account, isLoadingData]);

  // Network switching function
  const switchNetwork = useCallback(async (networkKey) => {
    if (!window.ethereum) {
      setError('Please install MetaMask or another Web3 wallet');
      return;
    }

    setIsSwitchingNetwork(true);
    setError('');

    // Notify parent component that network switch is starting
    if (onNetworkSwitch) {
      onNetworkSwitch();
    }

    try {
      const targetNetwork = networks[networkKey];
      
      // Check if we're already on the target network
      if (chainId === targetNetwork.chainId) {
        setSelectedNetwork(networkKey);
        setIsSwitchingNetwork(false);
        if (onNetworkSwitchComplete) {
          onNetworkSwitchComplete();
        }
        return;
      }

      // Try to switch network using window.ethereum directly
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${targetNetwork.chainId.toString(16)}` }]
      });
      
      setSelectedNetwork(networkKey);
      
      // Wait for network switch to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (switchError) {
      // If network doesn't exist, try to add it
      if (switchError.code === 4902) {
        try {
          const targetNetwork = networks[networkKey];
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${targetNetwork.chainId.toString(16)}`,
              chainName: targetNetwork.name,
              rpcUrls: [targetNetwork.rpcUrl],
              nativeCurrency: {
                name: networkKey === 'bsc' ? 'BNB' : 'XPH',
                symbol: networkKey === 'bsc' ? 'BNB' : 'XPH',
                decimals: 18
              },
              blockExplorerUrls: networkKey === 'bsc' 
                ? ['https://bscscan.com'] 
                : ['https://xp.tamsa.io']
            }]
          });
          setSelectedNetwork(networkKey);
          
          // Wait for network addition to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (addError) {
          console.error('Error adding network:', addError);
          setError(`Failed to add ${networks[networkKey].name} network`);
        }
      } else {
        console.error('Error switching network:', switchError);
        setError(`Failed to switch to ${networks[networkKey].name}`);
      }
    } finally {
      setIsSwitchingNetwork(false);
      // Notify parent component that network switch is complete
      if (onNetworkSwitchComplete) {
        onNetworkSwitchComplete();
      }
    }
  }, [chainId, networks, onNetworkSwitch, onNetworkSwitchComplete]);

  // Sync selectedNetwork with current chainId
  useEffect(() => {
    if (chainId === 56) {
      setSelectedNetwork('bsc');
    } else if (chainId === 20250217) {
      setSelectedNetwork('xphere');
    }
  }, [chainId]);

  const checkOwnership = useCallback(async () => {
    try {
      if (!currentNetwork) {
        console.error('❌ No network found for selectedNetwork:', selectedNetwork);
        setIsOwner(false);
        return;
      }
      
      const rpcProvider = getRpcProvider(currentNetwork.chainId, provider);
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, rpcProvider);
      const destBridgeContract = new ethers.Contract(currentNetwork.bridge, DESTINATION_BRIDGE_ABI, rpcProvider);
      const owner = await bridgeSourceContract.owner();
      const destOwner = await destBridgeContract.owner();
      
      setIsOwner(owner.toLowerCase() === account.toLowerCase());
      setOwnerAddresses(prev => ({
        ...prev,
        [currentNetwork.chainId]: {
          sourceOwner: owner,
          destinationOwner: destOwner
        }
      }));
    } catch (error) {
      console.error('❌ Error checking ownership:', error);
      setIsOwner(false);
    }
  }, [currentNetwork, provider, account, selectedNetwork]);

  // Refresh ownership check when selectedNetwork changes
  useEffect(() => {
    if (provider && signer && account && currentNetwork) {
      checkOwnership();
    }
  }, [selectedNetwork, currentNetwork, provider, signer, account, checkOwnership]);

  // Reset isLoadingData when currentNetwork changes
  useEffect(() => {
    console.log('🔄 Resetting isLoadingData due to currentNetwork change');
    setIsLoadingData(false);
    hasLoadedData.current = false; // Reset data loading flag when network changes
  }, [currentNetwork]);

  // Force reset isLoadingData on component mount
  useEffect(() => {
    console.log('🔄 Force resetting isLoadingData on component mount');
    setIsLoadingData(false);
  }, []);

  // Force trigger data loading when all conditions are met - MOVED AFTER FUNCTION DEFINITIONS

  const loadReserves = useCallback(async () => {
    try {
      if (!currentNetwork || !currentToken) {
        console.log('❌ No currentNetwork or currentToken for loadReserves');
        return;
      }
      
      console.log('🔄 Loading reserves for network:', currentNetwork.chainId, 'token:', currentToken.key);
      const rpcProvider = getRpcProvider(currentNetwork.chainId, provider);
      const bridgeContract = new ethers.Contract(currentNetwork.bridge, DESTINATION_BRIDGE_ABI, rpcProvider);
      const tokenAddress = currentToken.address;
      
      console.log('📊 Bridge contract:', currentNetwork.bridge);
      console.log('🪙 Token address:', tokenAddress);
      
      const reserveAmount = await bridgeContract.getTokenReserve(tokenAddress);
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
      const decimals = await tokenContract.decimals();
      
      const formattedAmount = ethers.formatUnits(reserveAmount, decimals);
      console.log('✅ Reserves loaded:', formattedAmount);
      
      const reserveKey = `${currentNetwork.chainId}-${currentToken.key}`;
      setReserves(prev => ({
        ...prev,
        [reserveKey]: formattedAmount
      }));
      
      // Store token decimals for this network-token combination
      setTokenDecimals(prev => ({
        ...prev,
        [reserveKey]: decimals
      }));
    } catch (error) {
      console.error('❌ Error loading reserves:', error);
      setError(`Failed to load reserves: ${error.message}`);
    }
  }, [currentNetwork, currentToken, provider]);

  const loadBalances = useCallback(async () => {
    try {
      if (!currentNetwork || !currentToken) {
        console.log('❌ No currentNetwork or currentToken for loadBalances');
        return;
      }
      
      console.log('🔄 Loading balances for network:', currentNetwork.chainId, 'token:', currentToken.key);
      const rpcProvider = getRpcProvider(currentNetwork.chainId, provider);
      const tokenAddress = currentToken.address;
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
      
      console.log('👤 Account:', account);
      console.log('🪙 Token address:', tokenAddress);
      
      const balance = await tokenContract.balanceOf(account);
      const decimals = await tokenContract.decimals();
      
      // Note: We don't mutate currentNetwork directly to avoid re-render loops
      console.log('✅ Token decimals:', decimals);
      
      const formattedBalance = ethers.formatUnits(balance, decimals);
      console.log('✅ Balance loaded:', formattedBalance);
      
      const balanceKey = `${currentNetwork.chainId}-${currentToken.key}`;
      setBalances(prev => ({
        ...prev,
        [balanceKey]: formattedBalance
      }));
      
      // Store token decimals for this network-token combination
      setTokenDecimals(prev => ({
        ...prev,
        [balanceKey]: decimals
      }));
    } catch (error) {
      console.error('❌ Error loading balances:', error);
      setError(`Failed to load balances: ${error.message}`);
    }
  }, [currentNetwork, currentToken, provider, account]);

  // Force trigger data loading when all conditions are met
  useEffect(() => {
    if (provider && signer && account && currentNetwork && currentToken && !isLoadingData && !hasLoadedData.current) {
      console.log('🚀 Force triggering data load - all conditions met');
      hasLoadedData.current = true;
      
      // Small delay to ensure state is settled
      const timeoutId = setTimeout(() => {
        if (provider && signer && account && currentNetwork && currentToken && !isLoadingData) {
          console.log('🚀 Actually starting data load now');
          setIsLoadingData(true);
          
          const loadData = async () => {
            try {
              console.log('🚀 Loading admin panel data...');
              await Promise.all([
                checkOwnership(),
                loadReserves(),
                loadBalances(),
                loadFeeCollector(),
                loadBridgeFee(),
                loadLockedTokenBalance(),
                loadTokenLimits(),
                loadDestinationDailyMintLimit()
              ]);
              console.log('✅ Admin panel data loaded successfully');
            } catch (error) {
              console.error('❌ Error loading admin panel data:', error);
            } finally {
              console.log('🔄 setIsLoadingData(false) called');
              setIsLoadingData(false);
            }
          };
          
          loadData();
        }
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [provider, signer, account, currentNetwork, currentToken, isLoadingData]); // eslint-disable-line react-hooks/exhaustive-deps

  // OLD useEffect - commented out to avoid conflicts
  // useEffect(() => {
  //   console.log('🔄 useEffect triggered with:', {
  //     provider: !!provider,
  //     signer: !!signer,
  //     account: !!account,
  //     currentNetwork: !!currentNetwork,
  //     isLoadingData
  //   });
  //   
  //   if (provider && signer && account && currentNetwork && !isLoadingData) {
  //     console.log('✅ All conditions met, starting to load data...');
  //     setIsLoadingData(true);
  //     console.log('🔄 setIsLoadingData(true) called');
  //     
  //     // Load data with a small delay to avoid rapid calls
  //     const loadData = async () => {
  //       try {
  //         console.log('🚀 Loading admin panel data...');
  //         await Promise.all([
  //           checkOwnership(),
  //           loadReserves(),
  //           loadBalances()
  //         ]);
  //         console.log('✅ Admin panel data loaded successfully');
  //       } catch (error) {
  //         console.error('❌ Error loading admin panel data:', error);
  //       } finally {
  //         console.log('🔄 setIsLoadingData(false) called');
  //         setIsLoadingData(false);
  //       }
  //     };
  //     
  //     const timeoutId = setTimeout(loadData, 100);
  //     return () => clearTimeout(timeoutId);
  //   } else {
  //     console.log('❌ Conditions not met for loading data');
  //     if (isLoadingData) {
  //       console.log('❌ isLoadingData is true, preventing data loading');
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [provider, signer, account, currentNetwork]);

  const addReserves = async () => {
    if (!currentNetwork || !currentToken) return;
    const amountKey = `${currentNetwork.chainId}-${currentToken.key}`;
    if (!amounts[amountKey]) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create fresh provider and signer for the current network
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();
      
      const tokenAddress = currentToken.address;
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, freshSigner);
      const bridgeContract = new ethers.Contract(currentNetwork.bridge, DESTINATION_BRIDGE_ABI, freshSigner);
      
      const decimals = await tokenContract.decimals();
      const amount = ethers.parseUnits(amounts[amountKey], decimals);
      
      // Check allowance
      const allowance = await tokenContract.allowance(account, currentNetwork.bridge);
      if (allowance < amount) {
        // Approve tokens
        const approveTx = await tokenContract.approve(currentNetwork.bridge, amount);
        await approveTx.wait();
      }
      
      // Add reserves
      const tx = await bridgeContract.addTokenReserves(tokenAddress, amount);
      await tx.wait();
      
      setSuccess(`Successfully added ${amounts[amountKey]} ${currentToken.symbol} to reserves`);
      setAmounts(prev => ({ ...prev, [amountKey]: '' }));
      loadReserves();
      loadBalances();
    } catch (error) {
      console.error('Error adding reserves:', error);
      setError(error.message || 'Failed to add reserves');
    } finally {
      setLoading(false);
    }
  };

  const removeReserves = async () => {
    if (!currentNetwork || !currentToken) return;
    const amountKey = `${currentNetwork.chainId}-${currentToken.key}`;
    if (!amounts[amountKey]) return;
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create fresh provider and signer for the current network
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();
      
      const tokenAddress = currentToken.address;
      const bridgeContract = new ethers.Contract(currentNetwork.bridge, DESTINATION_BRIDGE_ABI, freshSigner);
      
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, freshProvider);
      const tokenDecimals = await tokenContract.decimals();
      const amount = ethers.parseUnits(amounts[amountKey], tokenDecimals);
      
      // Remove reserves
      const tx = await bridgeContract.removeTokenReserves(tokenAddress, amount);
      await tx.wait();
      
      setSuccess(`Successfully removed ${amounts[amountKey]} ${currentToken.symbol} from reserves`);
      setAmounts(prev => ({ ...prev, [amountKey]: '' }));
      loadReserves();
      loadBalances();
    } catch (error) {
      console.error('Error removing reserves:', error);
      setError(error.message || 'Failed to remove reserves');
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (value) => {
    if (!currentNetwork || !currentToken) return;
    const amountKey = `${currentNetwork.chainId}-${currentToken.key}`;
    setAmounts(prev => ({ ...prev, [amountKey]: value }));
  };

  const handleMaxAmount = () => {
    if (!currentNetwork || !currentToken) return;
    const dataKey = `${currentNetwork.chainId}-${currentToken.key}`;
    
    if (reserveMode === 'add') {
      // For adding reserves, use user's balance
      const balance = balances[dataKey] || '0';
      setAmounts(prev => ({ ...prev, [dataKey]: balance }));
    } else {
      // For removing reserves, use contract's reserves
      const reserve = reserves[dataKey] || '0';
      setAmounts(prev => ({ ...prev, [dataKey]: reserve }));
    }
  };

  const loadFeeCollector = useCallback(async () => {
    try {
      if (!currentNetwork) {
        console.log('❌ No currentNetwork for loadFeeCollector');
        return;
      }
      console.log('🔄 Loading fee collector for network:', currentNetwork.chainId);
      
      const rpcProvider = new ethers.JsonRpcProvider(currentNetwork.rpcUrl);
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, rpcProvider);
      
      const currentFeeCollector = await bridgeSourceContract.feeCollector();
      console.log('✅ Fee collector loaded:', currentFeeCollector);
      
    } catch (error) {
      console.error('❌ Error loading fee collector:', error);
      setError(`Failed to load fee collector: ${error.message}`);
    }
  }, [currentNetwork]);

  const loadBridgeFee = useCallback(async () => {
    try {
      if (!currentNetwork) {
        console.log('❌ No currentNetwork for loadBridgeFee');
        return;
      }
      console.log('🔄 Loading bridge fee for network:', currentNetwork.chainId);
      
      const rpcProvider = new ethers.JsonRpcProvider(currentNetwork.rpcUrl);
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, rpcProvider);
      
      const currentBridgeFee = await bridgeSourceContract.bridgeFee();
      console.log('✅ Bridge fee loaded:', currentBridgeFee.toString());
      
      setBridgeFee(Number(currentBridgeFee));
    } catch (error) {
      console.error('❌ Error loading bridge fee:', error);
      setError(`Failed to load bridge fee: ${error.message}`);
    }
  }, [currentNetwork]);

  const loadLockedTokenBalance = useCallback(async () => {
    try {
      if (!currentNetwork || !currentToken) {
        console.log('❌ No currentNetwork or currentToken for loadLockedTokenBalance');
        return;
      }
      console.log('🔄 Loading locked token balance for network:', currentNetwork.chainId, 'token:', currentToken.key);
      
      const rpcProvider = new ethers.JsonRpcProvider(currentNetwork.rpcUrl);
      const tokenContract = new ethers.Contract(currentToken.address, ERC20_ABI, rpcProvider);
      
      const balance = await tokenContract.balanceOf(currentNetwork.bridgeSource);
      const decimals = await tokenContract.decimals();
      
      console.log('✅ Locked token balance loaded:', balance.toString());
      console.log('✅ Token decimals:', decimals);
      
      // Store raw balance and token decimals
      const balanceKey = `${currentNetwork.chainId}-${currentToken.key}`;
      setLockedTokenBalance(balance.toString());
      setTokenDecimals(prev => ({
        ...prev,
        [balanceKey]: decimals
      }));
    } catch (error) {
      console.error('❌ Error loading locked token balance:', error);
      setError(`Failed to load locked token balance: ${error.message}`);
    }
  }, [currentNetwork, currentToken]);

  const loadTokenLimits = useCallback(async () => {
    try {
      if (!currentNetwork || !currentToken) {
        console.log('❌ No currentNetwork or currentToken for loadTokenLimits');
        return;
      }
      console.log('🔄 Loading token limits for network:', currentNetwork.chainId, 'token:', currentToken.key);
      
      const rpcProvider = new ethers.JsonRpcProvider(currentNetwork.rpcUrl);
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, rpcProvider);
      
      const tokenInfo = await bridgeSourceContract.getTokenInfo(currentToken.address);
      console.log('✅ Token info loaded:', tokenInfo);
      
      // Use the decimals from the token info, with proper fallback for RWA Nova
      let tokenDecimals = tokenInfo.decimals;
      if (!tokenDecimals || tokenDecimals === 0) {
        tokenDecimals = currentToken.decimals;
      }
      // Final fallback: use 18 for RWA Nova, 6 for others
      if (!tokenDecimals || tokenDecimals === 0) {
        tokenDecimals = (currentToken.key === 'rwaNova' || currentToken.key === 'rwanova') ? 18 : 6;
      }
      console.log(`🔍 Token decimals for display: ${tokenDecimals} (tokenInfo: ${tokenInfo.decimals}, currentToken: ${currentToken.decimals})`);
      
      const limitsKey = `${currentNetwork.chainId}-${currentToken.key}`;
      setTokenLimits(prev => ({
        ...prev,
        [limitsKey]: {
          minAmount: ethers.formatUnits(tokenInfo.minAmount, tokenDecimals),
          maxAmount: ethers.formatUnits(tokenInfo.maxAmount, tokenDecimals),
          dailyLimit: ethers.formatUnits(tokenInfo.dailyLimit, tokenDecimals)
        }
      }));
    } catch (error) {
      console.error('❌ Error loading token limits:', error);
      setError(`Failed to load token limits: ${error.message}`);
    }
  }, [currentNetwork, currentToken]);

  // Load destination daily mint limit for current network's destination contract
  const loadDestinationDailyMintLimit = useCallback(async () => {
    try {
      if (!currentNetwork || !currentToken) {
        console.log('❌ No currentNetwork or currentToken for loadDestinationDailyMintLimit');
        return;
      }
      console.log('🔄 Loading destination daily mint limit for network:', currentNetwork.chainId, 'token:', currentToken.key);
      
      // Use current network's destination contract
      const currentChainId = currentNetwork.chainId;
      const contracts = CONTRACT_ADDRESSES[currentChainId];
      if (!contracts || !contracts.destBridge) {
        console.log('❌ Destination contract not found for chain:', currentChainId);
        return;
      }
      
      // Use the token address on the current network
      const tokenAddress = currentToken.address;
      
      if (!tokenAddress) {
        console.log('❌ Could not determine token address');
        return;
      }
      
      console.log(`🔍 Current chain: ${currentChainId}, Token: ${tokenAddress}`);
      
      // Get RPC provider for current chain
      const rpcUrl = currentChainId === 56 
        ? (process.env.REACT_APP_BSC_MAINNET_RPC_URL || 'https://bsc-dataseed.binance.org')
        : (process.env.REACT_APP_XPHERE_MAINNET_RPC_URL || 'https://xp-mainnet.rpc.xplorium.xyz');
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const destBridgeContract = new ethers.Contract(contracts.destBridge, DESTINATION_BRIDGE_ABI, provider);
      
      // Get current daily mint limit
      const dailyMintLimit = await destBridgeContract.dailyMintLimit(tokenAddress);
      
      // Determine decimals for the token
      let tokenDecimals = currentToken.decimals || 18;
      if (currentToken.key === 'rwaNova' || currentToken.key === 'rwanova') {
        tokenDecimals = parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18');
      } else if (currentToken.key === 'usdt') {
        tokenDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS || '6');
      } else if (currentToken.key === 'usdx') {
        tokenDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS || '6');
      }
      
      const limitsKey = `${currentNetwork.chainId}-${currentToken.key}`;
      setDestinationDailyMintLimits(prev => ({
        ...prev,
        [limitsKey]: {
          limit: ethers.formatUnits(dailyMintLimit, tokenDecimals),
          rawLimit: dailyMintLimit.toString(),
          tokenAddress: tokenAddress,
          chainId: currentChainId,
          decimals: tokenDecimals
        }
      }));
      
      console.log(`✅ Destination daily mint limit loaded: ${ethers.formatUnits(dailyMintLimit, tokenDecimals)} tokens`);
    } catch (error) {
      console.error('❌ Error loading destination daily mint limit:', error);
      setError(`Failed to load destination daily mint limit: ${error.message}`);
    }
  }, [currentNetwork, currentToken]);

  const setBridgeFeeAmount = async () => {
    if (!newBridgeFee || parseFloat(newBridgeFee) < 0 || parseFloat(newBridgeFee) > 1000) {
      setError('Please enter a valid fee (0-1000 basis points, max 10%)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create fresh provider and signer for the current network
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();
      
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, freshSigner);
      
      const feeInBasisPoints = Math.floor(parseFloat(newBridgeFee));
      console.log('🔄 Setting bridge fee to:', feeInBasisPoints, 'basis points');
      const tx = await bridgeSourceContract.setBridgeFee(feeInBasisPoints);
      console.log('📝 Transaction sent:', tx.hash);
      
      setSuccess('Transaction sent! Waiting for confirmation...');
      
      await tx.wait();
      console.log('✅ Bridge fee updated successfully');
      
      setSuccess(`Bridge fee updated successfully to ${feeInBasisPoints} basis points (${(feeInBasisPoints / 100).toFixed(2)}%)!`);
      setNewBridgeFee('');
      
      // Reload bridge fee
      await loadBridgeFee();
    } catch (error) {
      console.error('❌ Error setting bridge fee:', error);
      setError(`Failed to set bridge fee: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyWithdraw = async () => {
    if (!emergencyWithdrawAmount || parseFloat(emergencyWithdrawAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!currentNetwork || !currentToken) {
      setError('Please select a network and token');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create fresh provider and signer for the current network
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();
      
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, freshSigner);
      
      // Get token decimals dynamically
      const tokenContract = new ethers.Contract(currentToken.address, ERC20_ABI, freshProvider);
      const tokenDecimals = await tokenContract.decimals();
      
      // Convert amount to wei using actual token decimals
      const amountInWei = ethers.parseUnits(emergencyWithdrawAmount, tokenDecimals);
      
      console.log('🔄 Emergency withdrawing:', emergencyWithdrawAmount, currentToken.symbol);
      console.log('🔄 Amount in wei:', amountInWei.toString());
      console.log('🔄 Token address:', currentToken.address);
      
      const tx = await bridgeSourceContract.emergencyWithdraw(currentToken.address, amountInWei);
      console.log('📝 Transaction sent:', tx.hash);
      
      setSuccess('Emergency withdraw transaction sent! Waiting for confirmation...');
      
      await tx.wait();
      console.log('✅ Emergency withdraw successful');
      
      setSuccess(`Successfully withdrew ${emergencyWithdrawAmount} ${currentToken.symbol}!`);
      setEmergencyWithdrawAmount('');
      
      // Reload locked token balance
      await loadLockedTokenBalance();
    } catch (error) {
      console.error('❌ Error emergency withdrawing:', error);
      setError(`Failed to emergency withdraw: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMaxEmergencyWithdraw = async () => {
    if (!currentNetwork || !currentToken) return;
    if (lockedTokenBalance && lockedTokenBalance !== '0') {
      try {
        const balanceKey = `${currentNetwork.chainId}-${currentToken.key}`;
        // Use stored token decimals or fallback to token default
        const decimals = tokenDecimals[balanceKey] || currentToken.decimals || 18;
        
        // Convert from wei to human readable format using actual token decimals
        const maxAmount = ethers.formatUnits(lockedTokenBalance, decimals);
        setEmergencyWithdrawAmount(maxAmount);
      } catch (error) {
        console.error('Error formatting max amount:', error);
        // Fallback to token decimals
        const maxAmount = ethers.formatUnits(lockedTokenBalance, currentToken.decimals || 18);
        setEmergencyWithdrawAmount(maxAmount);
      }
    }
  };

  const setTokenLimitsAmount = async () => {
    // Check if at least one field is filled
    const hasMinAmount = newLimits.minAmount && newLimits.minAmount.trim() !== '';
    const hasMaxAmount = newLimits.maxAmount && newLimits.maxAmount.trim() !== '';
    const hasDailyLimit = newLimits.dailyLimit && newLimits.dailyLimit.trim() !== '';

    if (!hasMinAmount && !hasMaxAmount && !hasDailyLimit) {
      setError('Please enter at least one limit value to update');
      return;
    }

    // Validate filled fields
    if (hasMinAmount) {
      const minAmount = parseFloat(newLimits.minAmount);
      if (minAmount <= 0) {
        setError('Minimum amount must be greater than 0');
        return;
      }
    }

    if (hasMaxAmount) {
      const maxAmount = parseFloat(newLimits.maxAmount);
      if (maxAmount <= 0) {
        setError('Maximum amount must be greater than 0');
        return;
      }
    }

    if (hasDailyLimit) {
      const dailyLimit = parseFloat(newLimits.dailyLimit);
      if (dailyLimit <= 0) {
        setError('Daily limit must be greater than 0');
        return;
      }
    }

    if (!currentNetwork || !currentToken) {
      setError('Please select a network and token');
      return;
    }

    // Get current limits for validation
    const limitsKey = `${currentNetwork.chainId}-${currentToken.key}`;
    const currentLimits = tokenLimits[limitsKey];
    if (!currentLimits) {
      setError('Current limits not loaded. Please refresh and try again.');
      return;
    }

    // Use current values for fields that weren't updated
    const minAmount = hasMinAmount ? parseFloat(newLimits.minAmount) : parseFloat(currentLimits.minAmount);
    const maxAmount = hasMaxAmount ? parseFloat(newLimits.maxAmount) : parseFloat(currentLimits.maxAmount);
    const dailyLimit = hasDailyLimit ? parseFloat(newLimits.dailyLimit) : parseFloat(currentLimits.dailyLimit);

    // Validate relationships between limits
    if (minAmount >= maxAmount) {
      setError('Minimum amount must be less than maximum amount');
      return;
    }

    if (maxAmount >= dailyLimit) {
      setError('Maximum amount must be less than daily limit');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create fresh provider and signer for the current network
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();
      
      const bridgeSourceContract = new ethers.Contract(currentNetwork.bridgeSource, BRIDGE_SOURCE_ABI, freshSigner);
      
      // Get token decimals from the contract
      const tokenInfo = await bridgeSourceContract.getTokenInfo(currentToken.address);
      // Use tokenInfo decimals first, then currentToken decimals, with proper fallback
      // For RWA Nova, ensure we use 18 decimals, not 6
      let tokenDecimals = tokenInfo.decimals;
      if (!tokenDecimals || tokenDecimals === 0) {
        tokenDecimals = currentToken.decimals;
      }
      // Final fallback: use 18 for RWA Nova, 6 for others
      if (!tokenDecimals || tokenDecimals === 0) {
        tokenDecimals = (currentToken.key === 'rwaNova' || currentToken.key === 'rwanova') ? 18 : 6;
      }
      console.log(`🔍 Token decimals determined: ${tokenDecimals} (tokenInfo: ${tokenInfo.decimals}, currentToken: ${currentToken.decimals}, key: ${currentToken.key})`);
      
      // Use current values for empty fields, convert filled fields to wei
      const minAmountWei = hasMinAmount 
        ? ethers.parseUnits(newLimits.minAmount, tokenDecimals)
        : tokenInfo.minAmount;
      
      const maxAmountWei = hasMaxAmount 
        ? ethers.parseUnits(newLimits.maxAmount, tokenDecimals)
        : tokenInfo.maxAmount;
      
      const dailyLimitWei = hasDailyLimit 
        ? ethers.parseUnits(newLimits.dailyLimit, tokenDecimals)
        : tokenInfo.dailyLimit;
      
      console.log('🔄 Setting token limits:', {
        minAmount: hasMinAmount ? newLimits.minAmount : `${ethers.formatUnits(tokenInfo.minAmount, tokenDecimals)} (current)`,
        maxAmount: hasMaxAmount ? newLimits.maxAmount : `${ethers.formatUnits(tokenInfo.maxAmount, tokenDecimals)} (current)`,
        dailyLimit: hasDailyLimit ? newLimits.dailyLimit : `${ethers.formatUnits(tokenInfo.dailyLimit, tokenDecimals)} (current)`,
        tokenDecimals
      });
      
      const tx = await bridgeSourceContract.setTokenLimits(
        currentToken.address,
        minAmountWei,
        maxAmountWei,
        dailyLimitWei
      );
      console.log('📝 Transaction sent:', tx.hash);
      
      setSuccess('Transaction sent! Waiting for confirmation...');
      
      await tx.wait();
      console.log('✅ Token limits updated successfully');
      
      // Create success message showing what was updated
      const updatedFields = [];
      if (hasMinAmount) updatedFields.push(`Min: ${newLimits.minAmount}`);
      if (hasMaxAmount) updatedFields.push(`Max: ${newLimits.maxAmount}`);
      if (hasDailyLimit) updatedFields.push(`Daily: ${newLimits.dailyLimit}`);
      
      setSuccess(`Token limits updated successfully! ${updatedFields.join(', ')} ${currentToken.symbol}`);
      
      // Clear only the fields that were updated
      setNewLimits(prev => ({
        minAmount: hasMinAmount ? '' : prev.minAmount,
        maxAmount: hasMaxAmount ? '' : prev.maxAmount,
        dailyLimit: hasDailyLimit ? '' : prev.dailyLimit
      }));
      
      // Reload token limits
      await loadTokenLimits();
    } catch (error) {
      console.error('❌ Error setting token limits:', error);
      setError(`Failed to set token limits: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const setDestinationDailyMintLimit = async () => {
    if (!newDestinationDailyMintLimit || parseFloat(newDestinationDailyMintLimit) <= 0) {
      setError('Please enter a valid daily mint limit (greater than 0)');
      return;
    }

    if (!currentNetwork || !currentToken) {
      setError('Please select a network and token');
      return;
    }

    const limitsKey = `${currentNetwork.chainId}-${currentToken.key}`;
    const destLimitInfo = destinationDailyMintLimits[limitsKey];
    
    if (!destLimitInfo) {
      setError('Destination daily mint limit not loaded. Please refresh and try again.');
      return;
    }

    // Check if user is on the correct network (must be on current network)
    if (chainId !== currentNetwork.chainId) {
      const networkName = currentNetwork.chainId === 56 ? 'BSC' : 'Xphere';
      setError(`You must be connected to ${networkName} network to set the destination daily mint limit.`);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create fresh provider and signer for the current network
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();
      
      const contracts = CONTRACT_ADDRESSES[currentNetwork.chainId];
      if (!contracts || !contracts.destBridge) {
        throw new Error(`Destination contract not found for chain ${currentNetwork.chainId}`);
      }
      
      const destBridgeContract = new ethers.Contract(contracts.destBridge, DESTINATION_BRIDGE_ABI, freshSigner);
      
      // Get token decimals
      const tokenDecimals = destLimitInfo.decimals;
      
      // Convert to wei
      const limitWei = ethers.parseUnits(newDestinationDailyMintLimit, tokenDecimals);
      
      console.log('🔄 Setting destination daily mint limit:', {
        token: destLimitInfo.tokenAddress,
        limit: newDestinationDailyMintLimit,
        limitWei: limitWei.toString(),
        decimals: tokenDecimals,
        chainId: currentNetwork.chainId
      });
      
      const tx = await destBridgeContract.setDailyMintLimit(
        destLimitInfo.tokenAddress,
        limitWei
      );
      console.log('📝 Transaction sent:', tx.hash);
      
      setSuccess('Transaction sent! Waiting for confirmation...');
      
      await tx.wait();
      console.log('✅ Destination daily mint limit updated successfully');
      
      setSuccess(`Destination daily mint limit updated successfully to ${newDestinationDailyMintLimit} tokens!`);
      setNewDestinationDailyMintLimit('');
      
      // Reload destination daily mint limit
      await loadDestinationDailyMintLimit();
    } catch (error) {
      console.error('❌ Error setting destination daily mint limit:', error);
      if (error.message.includes('network') || error.message.includes('chain')) {
        setError(`Network mismatch: ${error.message}. Please ensure you're on ${currentNetwork.chainId === 56 ? 'BSC' : 'Xphere'} network.`);
      } else {
        setError(`Failed to set destination daily mint limit: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTransferOwnership = async (target) => {
    if (!newOwnerAddress || !ethers.isAddress(newOwnerAddress)) {
      setError('Please enter a valid address');
      return;
    }

    setTransferLoading(true);
    setError('');
    setSuccess('');

    try {
      const freshProvider = new ethers.BrowserProvider(window.ethereum);
      const freshSigner = await freshProvider.getSigner();

      const isSource = target === 'source';
      const contractAddress = isSource ? currentNetwork.bridgeSource : currentNetwork.bridge;
      const abi = isSource ? BRIDGE_SOURCE_ABI : DESTINATION_BRIDGE_ABI;
      const contract = new ethers.Contract(contractAddress, abi, freshSigner);

      const tx = await contract.transferOwnership(newOwnerAddress);
      setSuccess(`Ownership transfer submitted on ${isSource ? 'Source' : 'Destination'} contract. Waiting for confirmation...`);
      await tx.wait();

      setSuccess(`Ownership transferred on ${isSource ? 'Source' : 'Destination'} contract to ${newOwnerAddress}`);
      setNewOwnerAddress('');

      // Refresh owner info
      await checkOwnership();
    } catch (err) {
      console.error('❌ Error transferring ownership:', err);
      setError(`Failed to transfer ownership: ${err.message}`);
    } finally {
      setTransferLoading(false);
    }
  };

  const refreshData = async () => {
    setIsLoadingData(true);
    try {
      await Promise.all([
        loadReserves(),
        loadBalances(),
        checkOwnership(),
        loadFeeCollector(),
        loadBridgeFee(),
        loadLockedTokenBalance(),
        loadTokenLimits(),
        loadDestinationDailyMintLimit()
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Admin Access Required</h2>
            <p className="text-white/70">
              Only the contract owner can access the admin panel.
            </p>
            <p className="text-white/50 text-sm mt-2">
              Current account: {account}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // const tokenSymbol = chainId === 56 ? 'USDT' : 'USDX'; // Removed - using currentNetwork.tokenSymbol
  // const networkName = chainId === 56 ? 'BSC Mainnet' : 'Xphere Mainnet'; // Removed - using currentNetwork.name

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center space-x-2">
              <Shield size={24} />
              <span>Admin Panel</span>
            </h2>
            <p className="text-white/70">
              Manage token reserves across networks
            </p>
          </div>
          <button
            onClick={refreshData}
            disabled={isLoadingData}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={20} className={`text-white ${isLoadingData ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Network and Token Selector */}
        <div className="flex items-center space-x-4 flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <span className="text-white/70 text-sm">Select Network:</span>
            <div className="flex bg-white/10 rounded-lg p-1">
            <button
              onClick={() => switchNetwork('bsc')}
              disabled={isSwitchingNetwork}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2 ${
                selectedNetwork === 'bsc'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              } ${isSwitchingNetwork ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSwitchingNetwork && selectedNetwork === 'bsc' ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Switching...</span>
                </>
              ) : (
                <>
                  <img src="/bsc.svg" alt="BSC" className="w-4 h-4" />
                  <span>BSC Mainnet</span>
                </>
              )}
            </button>
            <button
              onClick={() => switchNetwork('xphere')}
              disabled={isSwitchingNetwork}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2 ${
                selectedNetwork === 'xphere'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              } ${isSwitchingNetwork ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSwitchingNetwork && selectedNetwork === 'xphere' ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Switching...</span>
                </>
              ) : (
                <>
                  <img src="/xpt.svg" alt="Xphere" className="w-4 h-4" />
                  <span>Xphere Mainnet</span>
                </>
              )}
            </button>
          </div>
          </div>
          
          {/* Token Selector */}
          {availableTokens.length > 0 && (
            <div className="flex items-center space-x-4">
              <span className="text-white/70 text-sm">Select Token:</span>
              <div className="flex bg-white/10 rounded-lg p-1">
                {availableTokens.map((token) => (
                  <button
                    key={token.key}
                    onClick={() => {
                      setSelectedToken(token.key);
                      hasLoadedData.current = false; // Reset to reload data for new token
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      selectedToken === token.key
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {token.symbol}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="text-red-500">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-green-500">{success}</span>
          </div>
        </div>
      )}

      {/* Reserve Management */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Status */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4">Current Status</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-white/70 text-sm">Contract Reserves</label>
              <div className="text-2xl font-bold text-white">
                {isLoadingData ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : currentToken && reserves[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                  `${parseFloat(reserves[`${currentNetwork.chainId}-${currentToken.key}`]).toLocaleString()} ${currentToken.symbol}`
                ) : (
                  'No data'
                )}
              </div>
            </div>
            
            <div>
              <label className="text-white/70 text-sm">Your Balance</label>
              <div className="text-lg font-semibold text-white">
                {isLoadingData ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : currentToken && balances[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                  `${parseFloat(balances[`${currentNetwork.chainId}-${currentToken.key}`]).toLocaleString()} ${currentToken.symbol}`
                ) : (
                  'No data'
                )}
              </div>
            </div>
            
            <div>
              <label className="text-white/70 text-sm">Contract Address</label>
              <div className="text-sm font-mono text-white/70 break-all">
                {currentNetwork?.bridge}
              </div>
            </div>
          </div>
        </div>

        {/* Reserve Actions */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-4">Reserve Management</h3>
          
          <div className="space-y-4">
            {/* Mode Selection */}
            <div>
              <label className="text-white/70 text-sm mb-2 block">Action</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => setReserveMode('add')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    reserveMode === 'add'
                      ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                      : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Add Reserves
                </button>
                <button
                  onClick={() => setReserveMode('remove')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    reserveMode === 'remove'
                      ? 'bg-red-500/20 border border-red-500/30 text-red-400'
                      : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Remove Reserves
                </button>
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label className="text-white/70 text-sm mb-2 block">Amount ({currentToken?.symbol || 'Token'})</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.000001"
                  placeholder="0.0"
                  value={currentToken ? (amounts[`${currentNetwork.chainId}-${currentToken.key}`] || '') : ''}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleMaxAmount}
                  className="px-4 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-400 font-medium transition-colors"
                >
                  Max
                </button>
              </div>
              <div className="mt-1 text-xs text-white/50">
                {reserveMode === 'add' ? (
                  <>Available: {currentToken ? (balances[`${currentNetwork.chainId}-${currentToken.key}`] || '0') : '0'} {currentToken?.symbol || ''}</>
                ) : (
                  <>Contract Reserves: {currentToken ? (reserves[`${currentNetwork.chainId}-${currentToken.key}`] || '0') : '0'} {currentToken?.symbol || ''}</>
                )}
              </div>
            </div>
            
            {/* Action Button */}
            <button
              onClick={reserveMode === 'add' ? addReserves : removeReserves}
              disabled={loading || !currentToken || !(currentToken ? amounts[`${currentNetwork.chainId}-${currentToken.key}`] : '')}
              className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                reserveMode === 'add'
                  ? 'bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400'
                  : 'bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400'
              }`}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : reserveMode === 'add' ? (
                <Plus className="h-4 w-4" />
              ) : (
                <Minus className="h-4 w-4" />
              )}
              <span>{reserveMode === 'add' ? 'Add Reserves' : 'Remove Reserves'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Fee Collector Management - HIDDEN */}
      {/* 
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4">Fee Collector Management</h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-2 block">Current Fee Collector</label>
            <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white/80 font-mono text-sm">
              {feeCollector || 'Loading...'}
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-2 block">New Fee Collector Address</label>
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="0x..."
                value={newFeeCollector}
                onChange={(e) => setNewFeeCollector(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              />
              <button
                onClick={setFeeCollectorAddress}
                disabled={loading || !newFeeCollector}
                className="px-6 py-3 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Set'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Bridge Fee Management */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4">Bridge Fee Management</h3>
        
        <div className="space-y-4">
          {/* Current Bridge Fee */}
          <div>
            <label className="text-white/70 text-sm mb-2 block">Current Bridge Fee</label>
            <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white/80 font-mono text-sm">
              {isLoadingData ? (
                <div className="flex items-center space-x-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : (
                `${bridgeFee} basis points (${(bridgeFee / 100).toFixed(2)}%)`
              )}
            </div>
            <div className="mt-1 text-xs text-white/50">
              Bridge fee is charged on each transaction and sent to the fee collector
            </div>
          </div>

          {/* Set New Bridge Fee */}
          <div>
            <label className="text-white/70 text-sm mb-2 block">New Bridge Fee (Basis Points)</label>
            <div className="flex space-x-2">
              <input
                type="number"
                min="0"
                max="1000"
                step="1"
                placeholder="50"
                value={newBridgeFee}
                onChange={(e) => setNewBridgeFee(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={setBridgeFeeAmount}
                disabled={loading || !newBridgeFee}
                className="px-6 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-blue-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Set'
                )}
              </button>
            </div>
            <div className="mt-1 text-xs text-white/50">
              Range: 0-1000 basis points (0-10%). Current: {bridgeFee} bp ({((bridgeFee || 0) / 100).toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>

      {/* Token Limits Management */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4">Token Limits Management</h3>
        
        <div className="space-y-4">
          {/* Current Token Limits */}
          <div>
            <label className="text-white/70 text-sm mb-2 block">Current Token Limits</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg">
                <div className="text-white/70 text-xs">Minimum Amount</div>
                <div className="text-white font-semibold">
                  {isLoadingData ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : currentToken && tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                    `${parseFloat(tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`].minAmount).toLocaleString()} ${currentToken.symbol}`
                  ) : (
                    'No data'
                  )}
                </div>
              </div>
              <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg">
                <div className="text-white/70 text-xs">Maximum Amount</div>
                <div className="text-white font-semibold">
                  {isLoadingData ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : currentToken && tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                    `${parseFloat(tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`].maxAmount).toLocaleString()} ${currentToken.symbol}`
                  ) : (
                    'No data'
                  )}
                </div>
              </div>
              <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg">
                <div className="text-white/70 text-xs">Daily Limit</div>
                <div className="text-white font-semibold">
                  {isLoadingData ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : currentToken && tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                    `${parseFloat(tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`].dailyLimit).toLocaleString()} ${currentToken.symbol}`
                  ) : (
                    'No data'
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 text-xs text-white/50">
              These limits control the minimum and maximum amounts users can bridge, and the daily volume limit
            </div>
          </div>

          {/* Set New Token Limits */}
          <div className="space-y-4">
            <div>
              <label className="text-white/70 text-sm mb-2 block">Set New Token Limits ({currentToken?.symbol || 'Token'})</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Minimum Amount</label>
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="20"
                    value={newLimits.minAmount}
                    onChange={(e) => setNewLimits(prev => ({ ...prev, minAmount: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Maximum Amount</label>
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="4999"
                    value={newLimits.maxAmount}
                    onChange={(e) => setNewLimits(prev => ({ ...prev, maxAmount: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Daily Limit</label>
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="50000"
                    value={newLimits.dailyLimit}
                    onChange={(e) => setNewLimits(prev => ({ ...prev, dailyLimit: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>
              </div>
              <div className="mt-2 text-xs text-white/50">
                You can update individual limits or all at once. Min &lt; Max &lt; Daily Limit. Current: Min: {currentToken ? (tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`]?.minAmount || 'N/A') : 'N/A'}, Max: {currentToken ? (tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`]?.maxAmount || 'N/A') : 'N/A'}, Daily: {currentToken ? (tokenLimits[`${currentNetwork.chainId}-${currentToken.key}`]?.dailyLimit || 'N/A') : 'N/A'}
              </div>
            </div>
            
            <button
              onClick={setTokenLimitsAmount}
              disabled={loading || (!newLimits.minAmount && !newLimits.maxAmount && !newLimits.dailyLimit)}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-green-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span>Update Token Limits</span>
            </button>
          </div>

          {/* Destination Daily Mint Limit Management */}
          <div className="border-t border-white/10 pt-6 mt-6">
            <h4 className="text-lg font-semibold text-white mb-4">Destination Daily Mint Limit</h4>
            <div className="text-xs text-white/50 mb-4">
              This controls the daily mint limit on the <strong>{currentNetwork.chainId === 56 ? 'BSC' : 'Xphere'} destination bridge contract</strong> for {currentToken?.symbol || 'the selected token'}.
              <span className="block mt-1">
                Set the daily mint limit for tokens on the current network's destination contract.
              </span>
            </div>

            {/* Current Destination Daily Mint Limit */}
            <div className="mb-4">
              <label className="text-white/70 text-sm mb-2 block">Current Destination Daily Mint Limit</label>
              <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg">
                <div className="text-white font-semibold">
                  {isLoadingData ? (
                    <div className="flex items-center space-x-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : currentToken && destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                    `${parseFloat(destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`].limit).toLocaleString()} tokens`
                  ) : (
                    'No data'
                  )}
                </div>
                {currentToken && destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`] && (
                  <div className="text-xs text-white/50 mt-1">
                    Network: {currentNetwork.chainId === 56 ? 'BSC' : 'Xphere'}
                    {' • '}
                    Token: {destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`].tokenAddress.substring(0, 10)}...
                    {' • '}
                    Decimals: {destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`].decimals}
                  </div>
                )}
              </div>
            </div>

            {/* Set New Destination Daily Mint Limit */}
            <div className="space-y-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Set New Destination Daily Mint Limit</label>
                <div className="flex space-x-3">
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="1000000"
                    value={newDestinationDailyMintLimit}
                    onChange={(e) => setNewDestinationDailyMintLimit(e.target.value)}
                    className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <button
                    onClick={setDestinationDailyMintLimit}
                    disabled={loading || !newDestinationDailyMintLimit}
                    className="px-6 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <span>Set</span>
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
                <div className="mt-2 text-xs text-white/50">
                  {currentToken && destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`] ? (
                    <>
                      Current: {parseFloat(destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`].limit).toLocaleString()} tokens
                      {' • '}
                      Decimals: {destinationDailyMintLimits[`${currentNetwork.chainId}-${currentToken.key}`].decimals}
                      {' • '}
                      Network: {currentNetwork.chainId === 56 ? 'BSC' : 'Xphere'}
                    </>
                  ) : (
                    `Enter the daily mint limit for ${currentToken?.symbol || 'the token'} on the ${currentNetwork.chainId === 56 ? 'BSC' : 'Xphere'} destination contract`
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Withdraw */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4">Emergency Withdraw</h3>
        
        <div className="space-y-4">
          {/* Locked Token Balance */}
          <div>
            <label className="text-white/70 text-sm mb-2 block">Locked Token Balance</label>
            <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white/80 font-mono text-sm">
              {isLoadingData ? (
                <div className="flex items-center space-x-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : currentToken && lockedTokenBalance && lockedTokenBalance !== '0' ? (
                (() => {
                  const balanceKey = `${currentNetwork.chainId}-${currentToken.key}`;
                  const decimals = tokenDecimals[balanceKey] || currentToken.decimals || 18;
                  const formattedBalance = ethers.formatUnits(lockedTokenBalance, decimals);
                  return `${parseFloat(formattedBalance).toLocaleString()} ${currentToken.symbol}`;
                })()
              ) : (
                `0 ${currentToken?.symbol || 'Token'}`
              )}
            </div>
          </div>

          {/* Withdraw Amount */}
          <div>
            <label className="text-white/70 text-sm mb-2 block">Withdraw Amount ({currentToken?.symbol || 'Token'})</label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.000001"
                placeholder="0.0"
                value={emergencyWithdrawAmount}
                onChange={(e) => setEmergencyWithdrawAmount(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button
                onClick={handleMaxEmergencyWithdraw}
                className="px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 font-medium transition-colors"
              >
                Max
              </button>
            </div>
          </div>

          {/* Emergency Withdraw Button */}
          <button
            onClick={handleEmergencyWithdraw}
            disabled={loading || !emergencyWithdrawAmount}
            className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Minus className="h-4 w-4" />
            )}
            <span>Emergency Withdraw</span>
          </button>
        </div>
      </div>

      {/* Transfer Ownership */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-xl font-bold text-white mb-4">Transfer Ownership</h3>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg">
            <div className="text-white/70 text-xs mb-1">Source Contract Owner</div>
            <div className="text-white font-mono text-sm break-all">
              {ownerAddresses[currentNetwork.chainId]?.sourceOwner || '—'}
            </div>
          </div>
          <div className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg">
            <div className="text-white/70 text-xs mb-1">Destination Contract Owner</div>
            <div className="text-white font-mono text-sm break-all">
              {ownerAddresses[currentNetwork.chainId]?.destinationOwner || '—'}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-white/70 text-sm mb-2 block">New Owner Address</label>
            <input
              type="text"
              placeholder="0x..."
              value={newOwnerAddress}
              onChange={(e) => setNewOwnerAddress(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <button
              onClick={() => handleTransferOwnership('source')}
              disabled={transferLoading || !newOwnerAddress}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg text-yellow-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {transferLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight size={14} />}
              <span>Transfer Source (BSC / XP)</span>
            </button>

            <button
              onClick={() => handleTransferOwnership('destination')}
              disabled={transferLoading || !newOwnerAddress}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {transferLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight size={14} />}
              <span>Transfer Destination (BSC / XP)</span>
            </button>
          </div>

          <p className="text-xs text-white/60">
            Applies to the selected network (BSC or XP). Make sure your wallet is connected to the correct chain before submitting.
          </p>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <h4 className="text-blue-400 font-semibold mb-2">Instructions:</h4>
        <ul className="text-white/70 text-sm space-y-1">
          <li>• <strong>Add Reserves:</strong> Transfer your tokens to the bridge contract for users to withdraw</li>
          <li>• <strong>Remove Reserves:</strong> Withdraw tokens from the bridge contract back to your wallet</li>
          {/* <li>• <strong>Fee Collector:</strong> Set the address that will receive bridge fees from transactions</li> */}
          <li>• <strong>Bridge Fee:</strong> Set the fee percentage (0-10%) charged on each bridge transaction</li>
          <li>• <strong>Token Limits:</strong> Configure minimum, maximum, and daily limits for bridge transactions (per token)</li>
          <li>• <strong>Emergency Withdraw:</strong> Withdraw locked tokens from the source bridge contract (emergency use only)</li>
          <li>• Make sure you have sufficient token balance before adding reserves</li>
          <li>• The contract will automatically approve tokens if needed</li>
          <li>• Bridge fee and token limits are configured separately for each network (BSC and Xphere)</li>
          <li>• Token limits must follow: Min Amount &lt; Max Amount &lt; Daily Limit</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPanel;
